import React, { useEffect, useState, useCallback } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  IconButton,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
  Publish as PublishIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import BirthdayGrid from '../components/BirthdayGrid';
import { useAuth } from '../contexts/auth';

const Dashboard = () => {
  const { user, validateToken } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(true);
  const [currentUserName, setCurrentUserName] = useState('');
  const [dashboardData, setDashboardData] = useState({
    stats: {
      residentes: 0,
      diariosCriadosHoje: 0,
      diariosPublicadosHoje: 0,
      familiaresInformados: 0
    },
    recentActivities: []
  });
  const [birthdays, setBirthdays] = useState([]);
  const [error, setError] = useState(null);

  // Verificar se o usuário tem lares vinculados
  const hasLinkedHomes = user?.profile === 'Administrador' || (user?.laresVinculados && user.laresVinculados.length > 0);

  // Mensagem informativa com base no perfil do usuário
  const getNoHomesMessage = () => {
    if (user?.profile === 'Gestor') {
      return 'Você não possui lares vinculados. Como gestor, você precisa estar vinculado a pelo menos um lar para visualizar informações completas do dashboard. Entre em contato com um administrador para vincular você a um ou mais lares.';
    } else if (user?.profile !== 'Administrador') {
      return 'Você não possui lares vinculados. Entre em contato com um administrador para vincular você a um ou mais lares.';
    }
    return '';
  };

  // Função para atualizar informações do usuário - usando useCallback para evitar recriações desnecessárias
  const updateUserInfo = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Dashboard: Atualizando informações do usuário...');
      
      // Validar o token e atualizar as informações do usuário
      const isValid = await validateToken();
      
      if (isValid) {
        console.log('Dashboard: Token validado com sucesso');
      } else {
        console.log('Dashboard: Token inválido, redirecionando para login');
        navigate('/login', {
          state: { authError: 'Sessão expirada. Por favor, faça login novamente.' },
          replace: true
        });
      }
    } catch (error) {
      console.error('Dashboard: Erro ao atualizar informações do usuário:', error);
      setError('Erro ao validar sessão. Por favor, atualize a página ou faça login novamente.');
    } finally {
      setLoading(false);
    }
  }, [validateToken, navigate]);

  // Atualizar informações do usuário ao carregar a página
  useEffect(() => {
    updateUserInfo();
  }, [updateUserInfo]);

  // Atualizar o nome do usuário quando o objeto user mudar
  useEffect(() => {
    if (user && user.name) {
      setCurrentUserName(user.name);
      console.log('Dashboard: Nome do usuário atualizado:', user.name);
    } else {
      setCurrentUserName('');
    }
  }, [user]);

  // Função para buscar dados do dashboard
  const fetchDashboardData = useCallback(async () => {
    if (!user) {
      console.log('Dashboard: Aguardando informações do usuário antes de buscar dados');
      return;
    }
    
    try {
      setDataLoading(true);
      console.log('Dashboard: Buscando dados do dashboard...');
      
      const [statsResponse, birthdaysResponse] = await Promise.all([
        api.get('/api/dashboard/stats'),
        api.get('/api/dashboard/birthdays')
      ]);
      
      if (!statsResponse.data || !statsResponse.data.stats) {
        console.error('Dashboard: Formato de resposta inválido:', statsResponse.data);
        throw new Error('Formato de resposta inválido');
      }

      console.log('Dashboard: Dados recebidos com sucesso');
      setDashboardData({
        stats: {
          residentes: statsResponse.data.stats.residentes || 0,
          diariosCriadosHoje: statsResponse.data.stats.diariosCriadosHoje || 0,
          diariosPublicadosHoje: statsResponse.data.stats.diariosPublicadosHoje || 0,
          familiaresInformados: statsResponse.data.stats.familiaresInformados || 0
        },
        recentActivities: statsResponse.data.recentActivities || []
      });

      setBirthdays(birthdaysResponse.data.birthdays || []);
      setError(null);
    } catch (error) {
      console.error('Dashboard: Erro ao buscar dados:', error);
      if (error.response) {
        console.error('Dashboard: Resposta de erro:', error.response.data);
        console.error('Dashboard: Status de erro:', error.response.status);
      }
      setError('Erro ao carregar dados do dashboard. Por favor, atualize a página.');
    } finally {
      setDataLoading(false);
    }
  }, [user]);

  // Buscar dados do dashboard quando o usuário estiver disponível
  useEffect(() => {
    if (!loading && user) {
      fetchDashboardData();
    }
  }, [loading, user, fetchDashboardData]);

  const stats = [
    {
      title: 'Residentes Ativos',
      value: dashboardData.stats.residentes,
      icon: <PersonIcon sx={{ fontSize: 40 }} />,
      color: '#1976d2'
    },
    {
      title: 'Diários Criados Hoje',
      value: dashboardData.stats.diariosCriadosHoje,
      icon: <AssignmentIcon sx={{ fontSize: 40 }} />,
      color: '#ed6c02'
    },
    {
      title: 'Diários Publicados Hoje',
      value: dashboardData.stats.diariosPublicadosHoje,
      icon: <PublishIcon sx={{ fontSize: 40 }} />,
      color: '#2e7d32'
    }
  ];

  const handleActivityClick = (activity) => {
    if (activity.type === 'novo_registro' && activity.diaryId) {
      navigate(`/diary/${activity.diaryId}`);
    }
  };

  // Se estiver carregando as informações do usuário, mostrar indicador de carregamento
  if (loading) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Validando sessão...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Dashboard</Typography>

      {/* Exibir mensagem de erro se houver */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
        >
          {error}
        </Alert>
      )}

      {/* Mensagem para usuários sem lares vinculados */}
      {!hasLinkedHomes && user?.profile !== 'Administrador' && (
        <Alert 
          severity="info" 
          icon={<InfoIcon />}
          sx={{ mb: 3 }}
        >
          {getNoHomesMessage()}
        </Alert>
      )}

      {dataLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Carregando dados...
          </Typography>
        </Box>
      ) : (
        <>
          <Typography variant="h4" gutterBottom>
            Bem-vindo(a), {currentUserName}
          </Typography>
          <Typography variant="subtitle1" color="textSecondary" gutterBottom>
            {user?.profile}
          </Typography>

          <Grid container spacing={3}>
            {stats.map((stat, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper
                  sx={{
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    height: '100%'
                  }}
                  elevation={3}
                >
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: `${stat.color}15`,
                      mb: 2
                    }}
                  >
                    <Box sx={{ color: stat.color }}>{stat.icon}</Box>
                  </Box>
                  <Typography variant="h4" component="div">
                    {stat.value}
                  </Typography>
                  <Typography variant="subtitle1" color="textSecondary" align="center">
                    {stat.title}
                  </Typography>
                </Paper>
              </Grid>
            ))}

            {/* Aniversariantes */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: '100%' }} elevation={3}>
                <Typography variant="h6" gutterBottom>
                  Aniversariantes do Mês
                </Typography>
                <BirthdayGrid birthdays={birthdays} />
              </Paper>
            </Grid>

            {/* Atividades Recentes */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: '100%' }} elevation={3}>
                <Typography variant="h6" gutterBottom>
                  Atividades Recentes
                </Typography>
                {dashboardData.recentActivities.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">
                    Nenhuma atividade recente encontrada.
                  </Typography>
                ) : (
                  <Box sx={{ mt: 2 }}>
                    {dashboardData.recentActivities.map((activity, index) => (
                      <Card 
                        key={index} 
                        sx={{ 
                          mb: 2, 
                          cursor: activity.diaryId ? 'pointer' : 'default',
                          '&:hover': activity.diaryId ? { bgcolor: 'rgba(0, 0, 0, 0.03)' } : {}
                        }}
                        onClick={() => activity.diaryId && handleActivityClick(activity)}
                      >
                        <CardHeader
                          avatar={
                            <Avatar sx={{ bgcolor: '#1976d2' }}>
                              {activity.type === 'novo_registro' ? <AssignmentIcon /> : <HomeIcon />}
                            </Avatar>
                          }
                          action={
                            activity.diaryId && (
                              <IconButton aria-label="view">
                                <NavigateNextIcon />
                              </IconButton>
                            )
                          }
                          title={activity.title}
                          subheader={`${activity.date} - ${activity.user}`}
                        />
                        <CardContent sx={{ pt: 0 }}>
                          <Typography variant="body2" color="textSecondary">
                            {activity.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default Dashboard;
